import { createSelector } from 'reselect';
import { selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { getActiveFolder, getViewOptions } from './options';
import { getState } from './state';
import { getBucketStreamId } from '../../utils';
import { PapiDrawerItem } from '../../types/papi';
import { getActiveDrawerStreamId, getLatestTs } from './drawer';
import { GROUP_FEED_BUCKET, USER_FEED_BUCKET } from '../../constants';

export const getDrawerStreamEntry = createSelector(
  [state => state, getActiveDrawerStreamId, (state, bucketId: string | undefined) => bucketId],
  (globalState, drawerStreamId, bucketId) => {
    if (bucketId && drawerStreamId) {
      const items = streamSelectors.getItems<PapiDrawerItem>(globalState, drawerStreamId);
      return items.find(item => item.bucket?.bucket === bucketId);
    }
    return undefined;
  }
);

export const getBucket = createSelector(
  [state => state, (state, bucketId: string | undefined) => bucketId],
  (globalState, bucketId) => getDrawerStreamEntry(globalState, bucketId)?.bucket
);

export const getBucketData = createSelector(
  [getState, (state, bucketId: string) => bucketId],
  (state, bucketId) => state.bucketData[bucketId]
);

const getActiveBucketId = createSelector(
  [getViewOptions],
  viewOptions => viewOptions.activeBucket?.bucket
);

export const getActiveBucket = createSelector(
  [state => state, getActiveBucketId, getLatestTs],
  (state, activeBucketId, latestTs) => {
    if (activeBucketId && [USER_FEED_BUCKET, GROUP_FEED_BUCKET].includes(activeBucketId)) {
      return {
        bucket: activeBucketId,
        ts: latestTs,
      };
    }
    return getBucket(state, activeBucketId);
  }
);

export const getActiveBucketHeader = createSelector(
  [state => state, getActiveBucket],
  (state, activeBucket) => {
    if (!activeBucket) {
      return undefined;
    }
    return getBucketData(state, activeBucket.bucket)?.header;
  }
);

export const getActiveBucketFilters = createSelector(
  [state => state, getActiveBucket],
  (state, activeBucket) => {
    if (!activeBucket) {
      return [];
    }
    return getBucketData(state, activeBucket.bucket)?.filters ?? [];
  }
);

export const getActiveBucketHasFilters = createSelector(
  [getActiveBucketFilters],
  filters => filters.length > 0
);

export const getActiveBucketStreamParams = createSelector(
  [getViewOptions, getActiveFolder],
  (viewOptions, activeFolder) => {
    const activeBucket = viewOptions.activeBucket;
    if (!activeBucket) {
      return {
        bucket: '',
      };
    }

    return {
      folder: activeFolder,
      bucket: activeBucket.bucket,
      filterId: activeBucket.filterId,
    };
  }
);

export const getActiveBucketStreamId = createSelector([getActiveBucketStreamParams], params => {
  if (params.bucket.length === 0) {
    return undefined;
  }
  return getBucketStreamId(params);
});

export const getActiveBucketFilterId = createSelector(
  [getViewOptions],
  viewOptions => viewOptions.activeBucket?.filterId
);

export const isAllFeedBucket = createSelector(
  [getActiveBucketId],
  activeBucketId => activeBucketId === USER_FEED_BUCKET || activeBucketId === GROUP_FEED_BUCKET
);
